/**
 * CORE-71 — раздел «Уведомления» в настройках: чем человек управляет.
 *
 * Список, а не пять копий разметки: строки различаются только ключом, значком
 * и текстом, и переписывать ради этого карточку пять раз значит завести пять
 * мест, где верстка может разойтись.
 *
 * Модуль намеренно свободен от React Native и от i18n-рантайма — здесь только
 * данные. Так его видно тестам (в этом репозитории компоненты RN под jest не
 * импортируются), а экран остаётся тем, что рисует, а не тем, что решает.
 */
import type { NotificationCategoryKey, NotificationSettings } from '../../types/auth';

export interface NotificationCategoryRow {
  /** Поле в {@link NotificationSettings} и в запросе на сохранение. */
  key: NotificationCategoryKey;
  /** Значок Ionicons. */
  icon: string;
  /** Ключ заголовка в namespace `settings`. */
  titleKey: string;
  /** Ключ пояснения — того самого, что описывает, о чём именно уведомление. */
  descKey: string;
  /** CORE-1811 — строка только для мастера: остальным выключать здесь нечего. */
  mastersOnly?: boolean;
}

/**
 * Порядок — из ТЗ, и он не алфавитный: сверху то, что приходит чаще всего,
 * снизу — системное, которое почти никто не трогает.
 */
export const NOTIFICATION_CATEGORIES: readonly NotificationCategoryRow[] = [
  {
    key: 'notifyMessages',
    icon: 'chatbubble-ellipses-outline',
    titleKey: 'notifications.catMessages',
    descKey: 'notifications.catMessagesHint',
  },
  // CORE-1811 — отклик на публикации мастера, рядом с «Сообщениями»: тоже
  // о том, что люди написали ему. Только мастеру: посты с
  // комментариями и реакциями, о которых сообщается, есть только у него.
  {
    key: 'notifyPostEngagement',
    icon: 'heart-outline',
    titleKey: 'notifications.catPostEngagement',
    descKey: 'notifications.catPostEngagementHint',
    mastersOnly: true,
  },
  {
    key: 'notifyMasterEvents',
    icon: 'calendar-outline',
    titleKey: 'notifications.catMasterEvents',
    descKey: 'notifications.catMasterEventsHint',
  },
  {
    key: 'notifyPracticeStart',
    icon: 'radio-outline',
    titleKey: 'notifications.catPracticeStart',
    descKey: 'notifications.catPracticeStartHint',
  },
  {
    key: 'notifyNewMedia',
    icon: 'play-circle-outline',
    titleKey: 'notifications.catNewMedia',
    descKey: 'notifications.catNewMediaHint',
  },
  {
    key: 'notifySystem',
    icon: 'information-circle-outline',
    titleKey: 'notifications.catSystem',
    descKey: 'notifications.catSystemHint',
  },
  // CORE-1453 — последним и сознательно: это не уведомление платформы, а
  // согласие на то, что ИИ заговорит первым. Пояснение обязано сказать, что
  // именно выключается, иначе человек решит, что глушит уведомления.
  {
    key: 'agapellaProactive',
    icon: 'sparkles-outline',
    titleKey: 'notifications.catAgapellaProactive',
    descKey: 'notifications.catAgapellaProactiveHint',
  },
  // CORE-1482 — рядом с «Агапелла пишет первой», потому что это тоже её
  // инициатива, но выключатель свой: продукт решил, что они независимы.
  // Пояснение обязано сказать, что закрытые от ИИ записи в итог не попадают, —
  // иначе выключат из опасения за дневник, а не потому, что итог не нужен.
  {
    key: 'agapellaWeeklySummary',
    icon: 'reader-outline',
    titleKey: 'notifications.catAgapellaWeeklySummary',
    descKey: 'notifications.catAgapellaWeeklySummaryHint',
  },
] as const;

/** CORE-1811 — строки, которые этому человеку есть смысл показывать. */
export function categoriesFor(isMaster: boolean): readonly NotificationCategoryRow[] {
  return isMaster ? NOTIFICATION_CATEGORIES : NOTIFICATION_CATEGORIES.filter((row) => !row.mastersOnly);
}

/**
 * Значение переключателя.
 *
 * Отсутствующее поле читается как «включено», а не как «выключено»: настройки
 * по умолчанию включены, и клиент постарше, разговаривающий с бэком без этих
 * полей, не должен показывать человеку, что всё выключено.
 */
export function isCategoryOn(
  settings: Pick<NotificationSettings, NotificationCategoryKey> | null | undefined,
  key: NotificationCategoryKey,
): boolean {
  const value = settings?.[key];
  return value === undefined || value === null ? true : value;
}

/**
 * Что уйдёт на сервер при нажатии.
 *
 * Один ключ, а не вся форма: PUT меняет только присланное, и слать соседние
 * значения значило бы затирать их тем, что экран прочитал когда-то раньше.
 */
export function toggleFor(
  key: NotificationCategoryKey,
  current: boolean,
): Record<string, boolean> {
  return { [key]: !current };
}
